/**
 * CurrencyIcon — Universal Currency/Asset Icon Component
 *
 * Automatically selects the correct icon type:
 * - Crypto → cryptocurrency-icons SVG
 * - Fiat → react-circle-flags
 * - Payment Method → custom SVG icons
 *
 * @example
 * <CurrencyIcon symbol="BTC" />        // Bitcoin logo
 * <CurrencyIcon symbol="USD" />        // US flag
 * <CurrencyIcon symbol="pix" />        // PIX icon
 * <CurrencyIcon symbol="ETH" size={32} />
 */

import { SFIcon } from '@/components/ui/sf-icon';
import { getCryptoMeta, isCrypto } from '@/lib/icons/crypto-icons';
import { getCountryCode, getFiatMeta, isFiat } from '@/lib/icons/currency-flags';
import {
  getPaymentMethodIcon,
  getPaymentMethodMeta,
  isPaymentMethod,
} from '@/lib/icons/payment-methods';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { CircleFlag } from 'react-circle-flags';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface CurrencyIconProps {
  /** Currency symbol (BTC, USD, etc.) or payment method (pix, sepa) */
  symbol: string;
  /** Icon size - maps to consistent pixel values */
  size?: IconSize | number;
  /** Use colored version (for payment methods) */
  colored?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show fallback icon if not found */
  showFallback?: boolean;
  /** Custom fallback component */
  fallback?: React.ReactNode;
  /** Use "long" version for large cards (if available for payment methods) */
  useLong?: boolean;
}

// Size mapping (Canon: 16/20/24/32/40/48)
const SIZE_MAP: Record<IconSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  '2xl': 48,
};

// Crypto icon style
type CryptoIconStyle = 'color' | 'white' | 'black' | 'icon';

/**
 * Get numeric size from size prop
 */
function getNumericSize(size: IconSize | number): number {
  if (typeof size === 'number') return size;
  return SIZE_MAP[size] || SIZE_MAP.md;
}

/**
 * CryptoIcon sub-component
 */
const CryptoIcon: React.FC<{
  symbol: string;
  size: number;
  style?: CryptoIconStyle;
  className?: string;
}> = ({ symbol, size, style = 'color', className }) => {
  // Defensive check for undefined/null symbol
  const normalizedSymbol = symbol?.toLowerCase() || 'generic';
  const meta = getCryptoMeta(symbol || '');
  const [useFallback, setUseFallback] = useState(false);
  const [triedGeneric, setTriedGeneric] = useState(false);

  // Handle image load error with fallback
  const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    // If we haven't tried generic.svg yet, try it
    if (!triedGeneric && !target.src.includes('generic.svg')) {
      setTriedGeneric(true);
      target.src = `/crypto-icons/${style}/generic.svg`;
    } else {
      // If generic also fails, use icon component fallback
      setUseFallback(true);
    }
  }, [triedGeneric, style]);

  // Use lucide-react Coins icon as final fallback
  if (useFallback) {
    return (
      <SFIcon icon={sfIcons.coins} className={cn('text-muted-foreground shrink-0', className)}
        aria-label={meta?.name || symbol.toUpperCase()}
      />
    );
  }

  // Use img tag to load from public folder
  // Add image-rendering for crisp icons (prevents blurry scaling)
  return (
    <img
      src={triedGeneric ? `/crypto-icons/${style}/generic.svg` : `/crypto-icons/${style}/${normalizedSymbol}.svg`}
      alt={meta?.name || symbol.toUpperCase()}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        imageRendering: 'crisp-edges', // Prevent blurry scaling (also works in Safari)
        objectFit: 'contain', // Ensure SVG scales to fit container
      }}
      className={cn('inline-block shrink-0', className)}
      onError={handleError}
      loading="eager" // Load icons immediately for better UX
    />
  );
};

/**
 * FiatIcon sub-component using react-circle-flags
 * Note: CircleFlag SVGs can expand in flex layouts, so we constrain with explicit dimensions
 */
const FiatIcon: React.FC<{
  symbol: string;
  size: number;
  className?: string;
}> = ({ symbol, size, className }) => {
  const countryCode = getCountryCode(symbol);

  if (!countryCode) {
    return null;
  }

  return (
    <span
      className={cn('inline-flex items-center justify-center shrink-0', className)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
      }}
    >
      <CircleFlag
        countryCode={countryCode}
        style={{
          imageRendering: 'crisp-edges',
        }}
      />
    </span>
  );
};

/**
 * PaymentMethodIcon sub-component
 */
const PaymentMethodIconWrapper: React.FC<{
  method: string;
  size: number;
  colored?: boolean;
  className?: string;
  useLong?: boolean; // Use "long" version for large cards (if available)
}> = ({ method, size, colored = false, className, useLong }) => {
  const IconComponent = getPaymentMethodIcon(method, colored);

  if (!IconComponent) {
    return null;
  }

  return <IconComponent size={size} useLong={useLong} className={className} />;
};

/**
 * Main CurrencyIcon component
 */
export const CurrencyIcon: React.FC<CurrencyIconProps> = ({
  symbol,
  size = 'md',
  colored = true,
  className,
  showFallback = true,
  fallback,
  useLong, // Use "long" version for large cards (if available)
}) => {
  const numericSize = getNumericSize(size);

  // Determine icon type - must be before any early returns (rules of hooks)
  const iconType = useMemo(() => {
    if (!symbol) return 'unknown';
    if (isCrypto(symbol)) return 'crypto';
    if (isFiat(symbol)) return 'fiat';
    if (isPaymentMethod(symbol)) return 'payment';
    return 'unknown';
  }, [symbol]);

  // Defensive check: return fallback if symbol is undefined/null/empty
  if (!symbol) {
    if (!showFallback) return null;
    if (fallback) return <>{fallback}</>;
    return (
      <SFIcon icon={sfIcons.coins} className={cn('text-muted-foreground', className)}
        aria-label="Unknown currency"
      />
    );
  }

  const normalizedSymbol = symbol.toLowerCase();

  // Render appropriate icon
  switch (iconType) {
    case 'crypto':
      return (
        <CryptoIcon
          symbol={normalizedSymbol}
          size={numericSize}
          style={colored ? 'color' : 'black'}
          className={className}
        />
      );

    case 'fiat':
      return (
        <FiatIcon
          symbol={normalizedSymbol}
          size={numericSize}
          className={className}
        />
      );

    case 'payment':
      return (
        <PaymentMethodIconWrapper
          method={normalizedSymbol}
          size={numericSize}
          colored={colored}
          className={className}
          useLong={useLong}
        />
      );

    default:
      if (!showFallback) return null;
      if (fallback) return <>{fallback}</>;
      return (
        <SFIcon icon={sfIcons.coins} className={cn('text-muted-foreground', className)}
          aria-label={`${symbol} icon`}
        />
      );
  }
};

/**
 * Get display info for any currency/asset
 */
export function getCurrencyDisplayInfo(symbol: string): {
  name: string;
  symbol: string;
  type: 'crypto' | 'fiat' | 'payment' | 'unknown';
  color?: string;
  country?: string;
} | null {
  const normalized = symbol.toLowerCase();

  if (isCrypto(symbol)) {
    const meta = getCryptoMeta(symbol);
    if (meta) {
      return {
        name: meta.name,
        symbol: meta.symbol,
        type: 'crypto',
        color: meta.color,
      };
    }
  }

  if (isFiat(symbol)) {
    const meta = getFiatMeta(symbol);
    if (meta) {
      return {
        name: meta.name,
        symbol: meta.symbol,
        type: 'fiat',
        country: meta.country,
      };
    }
  }

  if (isPaymentMethod(symbol)) {
    const meta = getPaymentMethodMeta(symbol);
    if (meta) {
      return {
        name: meta.fullName,
        symbol: meta.name,
        type: 'payment',
        country: meta.country,
      };
    }
  }

  return null;
}

/**
 * CurrencyIconWithLabel — Icon + Text combo
 */
export const CurrencyIconWithLabel: React.FC<{
  symbol: string;
  showName?: boolean;
  size?: IconSize | number;
  className?: string;
}> = ({ symbol, showName = false, size = 'md', className }) => {
  const info = getCurrencyDisplayInfo(symbol);

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <CurrencyIcon symbol={symbol} size={size} />
      <span className="font-medium">
        {showName && info ? info.name : symbol.toUpperCase()}
      </span>
    </div>
  );
};

export default CurrencyIcon;
